import { useRef, useState } from 'react'

import { Box, Button, Tabs } from '@factorialco/deprecated-design-system'
import type moment from 'moment'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useAddShiftPreview } from 'modules/shift_management/hooks/useAddShiftPreview'
import useCommonSelectors from 'modules/shift_management/hooks/useCommonSelectors'
import useCustomShiftForm from 'modules/shift_management/hooks/useCustomShiftForm'
import useDefaultShiftForms from 'modules/shift_management/hooks/useDefaultShiftForms'
import { useMutateDefaultShifts } from 'modules/shift_management/hooks/useMutateDefaultShifts'
import { useSaveShift } from 'modules/shift_management/hooks/useSaveShift'
import DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import { TABS, type TabsType } from 'modules/shift_management/lib/types'
import { Leave } from 'modules/shift_management/lib/types'

import CustomShiftSection from './CustomShiftSection'
import SavedShiftsSection from './SavedShiftsSection'

interface Props {
  onClose: () => void
  date?: moment.Moment
  noSavedShifts?: boolean
  advancedOptions?: boolean
  isBackup?: boolean
  leave?: Leave
  contractVersions: ContractVersions
}

const AddShiftSidepanel = ({
  onClose,
  date,
  noSavedShifts,
  advancedOptions = false,
  isBackup = false,
  leave,
  contractVersions,
}: Props) => {
  const [tab, setTab] = useState<TabsType>(noSavedShifts ? TABS.CUSTOM_SHIFT : TABS.SAVED_SHIFTS)
  const isSavedShiftsTab = tab === TABS.SAVED_SHIFTS
  const defaultShiftForm = useLazyRef(() => new DefaultShiftForm()).current
  const [showSavedShiftsError, setShowSavedShiftsError] = useState(false)
  const [trackSearch, setTrackSearch] = useState<string>('')
  const scrollToErrorsTimeout = useRef<ReturnType<typeof setTimeout>>()

  const [hasSeenBackupShiftsCallout, setHasSeenBackupShiftsCallout] = useState<boolean>(false)

  const {
    commonSelectorsForm,
    defaultAttributes,
    loadingEmployees: loading,
    selectedEmployees,
    setSelectedEmployees,
    selectableEmployees,
    weekDays,
    daysByEmployee,
  } = useCommonSelectors({ date, leave, contractVersions })

  const { plannedBreakForms, onAddBreak, onDeleteBreak, useSubmitCustomShift, customShiftForm } =
    useCustomShiftForm({ daysByEmployee, isBackup, leave, contractVersions })

  const { onSubmit: onSubmitCustomShift, submitting: submittingCustomShift } = useSubmitCustomShift(
    {
      defaultAttributes,
      weekDays,
      daysByRow: daysByEmployee,
      from: 'side_panel',
      advancedOptions,
    }
  )

  const { forms: defaultShiftForms, onToggleShift } = useDefaultShiftForms()

  const { onSubmit: onSubmitDefaultShifts, loading: submittingDefaultShifts } =
    useMutateDefaultShifts({
      defaultAttributes,
      forms: defaultShiftForms.filter((form) => form.get('is_selected').value),
      daysByRow: daysByEmployee,
      weekDays,
      from: 'side_panel',
      withSearch: trackSearch,
      advancedOptions,
      contractVersions,
    })

  const { onSubmit: onSubmitSaveShift, submitting: submittingSavedShift } = useSaveShift({
    customShiftForm,
    defaultShiftForm,
    plannedBreakForms,
    // Skip creating breaks because we create them from the default shift in the backend async.
    onSuccess: (defaultShiftId) => onSubmitCustomShift({ defaultShiftId, createBreaks: false }),
  })

  const { cleanPreview } = useAddShiftPreview({
    daysByRow: daysByEmployee,
    customShiftForm,
    defaultShiftForms,
    plannedBreakForms,
    selectedTab: tab,
    isBackup,
  })

  const handleErrors = (): boolean => {
    const isCommonValid = commonSelectorsForm.validate()
    if (isSavedShiftsTab) {
      const hasSelected = defaultShiftForms.some((form) => form.isSelected)
      hasSelected ? setShowSavedShiftsError(false) : setShowSavedShiftsError(true)

      return isCommonValid && hasSelected
    } else {
      const isCustomValid = customShiftForm.validate()
      const isSavedFromCustomValid = defaultShiftForm.validateSavedFromCustom()
      const isBreaksValid = plannedBreakForms.every((form) =>
        form.validate({
          startAt: customShiftForm.startAt,
          endAt: customShiftForm.endAt,
        })
      )

      return isCommonValid && isCustomValid && isSavedFromCustomValid && isBreaksValid
    }
  }

  const scrollToErrors = () => {
    // Timeout to wait for the error to be rendered
    clearTimeout(scrollToErrorsTimeout.current)
    scrollToErrorsTimeout.current = setTimeout(() => {
      const errorAnchors = document.getElementsByClassName('error-anchor')
      const firstError = errorAnchors[0]
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 100)
  }

  const handleOnSubmitCustomShift = () => {
    if (defaultShiftForm.isSavedFromCustom) {
      onSubmitSaveShift()
    } else {
      onSubmitCustomShift()
    }
  }

  const handleClose = () => {
    cleanPreview()
    onClose()
  }

  const onSubmit = () => {
    const isValid = handleErrors()
    if (!isValid) return scrollToErrors()

    isSavedShiftsTab ? onSubmitDefaultShifts() : handleOnSubmitCustomShift()
    handleClose()
  }

  const selectedShiftsCount = defaultShiftForms.filter((f) => f.get('is_selected').value).length
  const shiftsCount = isSavedShiftsTab && !!selectedShiftsCount ? selectedShiftsCount : 1
  const shiftsToAddCount = selectedEmployees.length === 1 ? shiftsCount : selectedEmployees.length
  const submitting = submittingCustomShift || submittingDefaultShifts || submittingSavedShift

  const handleChangeTab = () => setTab(TABS.CUSTOM_SHIFT)

  return (
    <Sidepanel
      open
      locked={submitting}
      title={i18n.t('shift_management.actions.add_shift')}
      footer={
        <>
          <Button onClick={handleClose} disabled={submitting} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={submitting} submit>
            {i18n.tp('shift_management.actions_sidepanel.add_shift', { count: shiftsToAddCount })}
          </Button>
        </>
      }
      onClose={handleClose}
      closeLabel="Close"
    >
      {loading ? (
        <Loading />
      ) : (
        <Box gap="s16">
          {isBackup && !hasSeenBackupShiftsCallout && (
            <Callout
              title={i18n.t('shift_management.backup_shifts.title')}
              description={i18n.t('shift_management.backup_shifts.creation')}
              type="info"
              onRemove={() => setHasSeenBackupShiftsCallout(true)}
            />
          )}
          <Tabs
            tabsAlign="center"
            onTabClicked={(tabId: TabsType) => setTab(tabId)}
            currentId={tab}
          >
            <Tabs.Tab
              id={TABS.SAVED_SHIFTS}
              label={i18n.t('shift_management.actions_sidepanel.tabs.saved_shift')}
            >
              <Box paddingTop="s24" gap="s24">
                <SavedShiftsSection
                  forms={defaultShiftForms}
                  commonSelectorsForm={commonSelectorsForm}
                  defaultAttributes={defaultAttributes}
                  selectableEmployees={selectableEmployees}
                  selectedEmployees={selectedEmployees}
                  setSelectedEmployees={setSelectedEmployees}
                  onToggleShift={(id) => {
                    showSavedShiftsError && setShowSavedShiftsError(false)
                    onToggleShift(id)
                  }}
                  handleChangeTab={handleChangeTab}
                  setTrackSearch={setTrackSearch}
                  showError={showSavedShiftsError}
                  leave={leave}
                  contractVersions={contractVersions}
                />
              </Box>
            </Tabs.Tab>
            <Tabs.Tab
              id={TABS.CUSTOM_SHIFT}
              label={i18n.t('shift_management.actions_sidepanel.tabs.custom_shift')}
            >
              <Box paddingTop="s24" gap="s24">
                <CustomShiftSection
                  form={customShiftForm}
                  defaultShiftForm={defaultShiftForm}
                  plannedBreakForms={plannedBreakForms}
                  onAddBreak={onAddBreak}
                  onDeleteBreak={onDeleteBreak}
                  commonSelectorsForm={commonSelectorsForm}
                  selectableEmployees={selectableEmployees}
                  selectedEmployees={selectedEmployees}
                  setSelectedEmployees={setSelectedEmployees}
                  leave={leave}
                  contractVersions={contractVersions}
                />
              </Box>
            </Tabs.Tab>
          </Tabs>
        </Box>
      )}
    </Sidepanel>
  )
}

export default observer(AddShiftSidepanel)
